// FT X — role-based synthesis of a player's key stats into a single 1..30 score.
// Percentages are scaled against a realistic "reference" (ref) that maps an elite
// value to 1.0 (e.g. 28% goal conversion ~ elite). Volume stats (goals, assists,
// exits) scale against a season cap. The weighted mean of the available factors is
// mapped to a 1..30 bar. Offensive roles weight goals/assists heavily.
const SAVE = { label: "Save rate", kind: "pct", ref: 0.75, match: ["save_rate_pct", "save_rate"] };
const EXIT = { label: "Exits", kind: "count", cap: 120, match: ["exits_per_90", "exits"] };
const ACC = { label: "Accurate passes %", kind: "pct", ref: 0.85, match: ["accurate_passes_pct", "accurate_passes"] };
const DUELS = { label: "Duels won %", kind: "pct", ref: 0.6, match: ["duels_won_pct", "duels_won", "duels"] };
const DEFD = { label: "Def. duels won %", kind: "pct", ref: 0.6, match: ["defensive_duels_won_pct", "defensive_duels_won", "defensive_duels"] };
const AER = { label: "Aerial duels %", kind: "pct", ref: 0.55, match: ["aerial_duels_won_pct", "aerial_duels_won", "aerial_duels"] };
const OFFD = { label: "Offensive duel %", kind: "pct", ref: 0.55, match: ["offensive_duels_won_pct", "offensive_duels_won", "offensive_duels"] };
const SOT = { label: "Shots on target %", kind: "pct", ref: 0.55, match: ["shots_on_target_pct", "shots_on_target"] };
const DRIB = { label: "Successful dribbles %", kind: "pct", ref: 0.55, match: ["successful_dribbles_pct", "successful_dribbles", "dribbles"] };
const GCONV = { label: "Goal conversion %", kind: "pct", ref: 0.28, match: ["goal_conversion_pct", "goal_conversion"] };
const GOAL = { label: "Goals", kind: "count", cap: 15, field: "goals", match: ["goals"] };
const ASSIST = { label: "Assists", kind: "count", cap: 10, field: "assists", match: ["assists"] };
const CLEAN = { label: "Clean sheets", kind: "count", cap: 12, match: ["clean_sheets"] };
const CONC90 = { label: "Conceded goals/90", kind: "invrate", bad: 2.5, match: ["conceded_goals_per_90", "conceded_goals"] };
const INTER = { label: "Interceptions/90", kind: "count", cap: 10, match: ["interceptions_per_90", "interceptions"] };
const ACCLONG = { label: "Accurate long passes %", kind: "pct", ref: 0.6, match: ["accurate_long_passes_pct", "accurate_long_passes"] };
const TOUCHBOX = { label: "Touches in box/90", kind: "count", cap: 12, match: ["touches_in_box_per_90", "touches_in_box"] };

const W = (def, weight) => ({ ...def, weight });

export const FTX_ROLE_STATS = {
  // GK value driven by save rate, accurate passes %, clean sheets and conceded goals/90;
  // exits capped at 10% of the weight (1 of total 10).
  GK: [W(SAVE, 3), W(ACC, 2), W(CLEAN, 2), W(CONC90, 2), W(EXIT, 1)],
  RCB: [W(DEFD, 2), AER, ACC, GOAL],
  LCB: [W(DEFD, 2), AER, ACC, GOAL],
  RB: [W(DEFD, 2), AER, ACC, GOAL, ASSIST],
  LB: [W(DEFD, 2), AER, ACC, GOAL, ASSIST],
  // CM: goals+assists count, plus heavy passing accuracy & shots on target, duels weighted
  CM: [W(GOAL, 2), W(ASSIST, 2), W(ACC, 2), W(SOT, 2), W(DUELS, 1)],
  // Wingers: goals+assists, plus successful dribbles & offensive duels
  RW: [W(GOAL, 2), W(ASSIST, 2), W(DRIB, 2), W(OFFD, 2), W(DUELS, 1)],
  LW: [W(GOAL, 2), W(ASSIST, 2), W(DRIB, 2), W(OFFD, 2), W(DUELS, 1)],
  // Strikers/Forwards: goals heavily, assists fairly, shots on target & goal conversion
  ST: [W(GOAL, 3), W(ASSIST, 2), W(SOT, 1), W(GCONV, 1)],
  FW: [W(GOAL, 3), W(ASSIST, 2), W(SOT, 1), W(GCONV, 1)],
};

export const FTX_MAX = 30;

// Full catalogue of selectable metrics, keyed by their display label. The editor
// lets the user add/replace/delete any of these on a per-role basis.
export const METRIC_CATALOG = Object.fromEntries(
  [SAVE, EXIT, ACC, DUELS, DEFD, AER, OFFD, SOT, DRIB, GCONV, GOAL, ASSIST, CLEAN, CONC90, INTER, ACCLONG, TOUCHBOX].map((d) => [d.label, d])
);
// [{ label }] for the "add metric" dropdown.
export function ftxMetricCatalog(metrics = []) {
  return Object.values(fullCatalogMap(metrics)).map((d) => ({ label: d.label }));
}

// Build a FT X normalisation def for an imported metric that isn't in the base catalog.
function inferDef(slug, label) {
  const s = (slug || "").toLowerCase();
  const lowerBetter = s.includes("conceded") || s.includes("against") || s.includes("red_card") || s.includes("yellow_card");
  if (s.endsWith("_pct") || (label || "").includes("%")) return { kind: "pct", ref: 0.6, match: [slug] };
  if (lowerBetter) {
    const bad = s.includes("per_90") ? 3 : s.includes("card") ? 8 : s === "conceded_goals" ? 40 : s === "shots_against" ? 400 : 20;
    return { kind: "invrate", bad, match: [slug] };
  }
  const CAP = { appearances: 38, minutes: 3400, shots: 80, height: 195, long_passes_per_90: 70, received_long_passes_per_90: 40, progressive_runs_per_90: 12, aerial_duels_per_90: 12 };
  const cap = CAP[s] || (s.includes("per_90") ? 12 : 30);
  const BASE_FIELDS = { appearances: "appearances", minutes: "minutes", height: "height", goals: "goals", assists: "assists" };
  const def = { kind: "count", cap, match: [slug] };
  if (BASE_FIELDS[s]) def.field = BASE_FIELDS[s];
  return def;
}

// Merge the hardcoded catalog with every imported metric (keyed by label) so that
// ALL stats present in the data are selectable in the FT X weights editor.
function fullCatalogMap(metrics = []) {
  const map = {};
  const covered = new Set();
  Object.values(METRIC_CATALOG).forEach((d) => {
    map[d.label] = d;
    if (d.field) covered.add(d.field);
    (d.match || []).forEach((f) => covered.add(f));
  });
  (metrics || []).forEach((m) => {
    if (!m || !m.slug || covered.has(m.slug)) return;
    const label = (m.label || m.slug).trim();
    if (map[label]) return;
    map[label] = { label, ...inferDef(m.slug, label) };
  });
  return map;
}

// Custom per-role weight overrides: { role: { metricLabel: weight } }. Loaded from backend at startup.
// When a role has a non-empty override object, its KEYS define which metrics are active
// for that role (enabling add / replace / delete); otherwise the built-in defaults apply.
let WEIGHT_OVERRIDES = {};
export function setFtxWeights(o) { WEIGHT_OVERRIDES = (o && typeof o === "object") ? o : {}; }
export function getFtxWeights() { return WEIGHT_OVERRIDES; }

// ---- League difficulty coefficient (applied to the final FT X, when enabled) ----
// The coefficient reflects the CLUB's country/league, never the player's nationality.
let LEAGUE_COEFF_ENABLED = false;
// Clubs not present in the club→league map get this automatic coefficient.
export const UNMAPPED_COEFF = 0.8;
export function setLeagueCoeffEnabled(v) { LEAGUE_COEFF_ENABLED = !!v; }
export function getLeagueCoeffEnabled() { return LEAGUE_COEFF_ENABLED; }

// Admin-editable overlay on top of the built-in table (persisted in settings):
//  _coeffOverrides: { "<rowId>": number }  — replaces a built-in value
//  _customLeagues: [{ label, match:[keywords], coeff }] — matched before built-ins
let _coeffOverrides = {};
let _customLeagues = [];
export function setLeagueConfig({ overrides, custom } = {}) {
  if (overrides && typeof overrides === "object") _coeffOverrides = overrides;
  if (Array.isArray(custom)) _customLeagues = custom;
}

// ---- Club → country/league map (so the coefficient follows the CLUB, not nationality) ----
const CLUB_STOP = new Set("women womens woman ladies fem femminile feminine femenino feminino frauen fc afc wfc cf acf ac as us ssc ssd asd sc cd cp calcio club kvinner dames vrouwen kvinde".split(" "));
function _clubTokens(name) {
  const n = (name || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return n.replace(/[^a-z0-9]+/g, " ").split(/\s+/).filter((t) => t && !CLUB_STOP.has(t));
}
let _CLUB_INDEX = { byKey: new Map(), list: [] };
function _buildClubIndex(arr) {
  const byKey = new Map();
  const list = [];
  (Array.isArray(arr) ? arr : []).forEach((e) => {
    const toks = _clubTokens(e.club);
    if (!toks.length) return;
    const rec = { key: toks.join(""), toks: new Set(toks), country: e.country || "", league: e.league || "", club: e.club || "" };
    if (!byKey.has(rec.key)) byKey.set(rec.key, rec);
    list.push(rec);
  });
  return { byKey, list };
}
function _resolveInIndex(index, team) {
  const toks = _clubTokens(team);
  if (!toks.length) return null;
  const exact = index.byKey.get(toks.join(""));
  if (exact) return { country: exact.country, league: exact.league, club: exact.club };
  const pset = new Set(toks);
  let best = null, bestOverlap = 0;
  const countries = new Set();
  for (const rec of index.list) {
    const sub = [...pset].every((t) => rec.toks.has(t)) || [...rec.toks].every((t) => pset.has(t));
    if (!sub) continue;
    countries.add((rec.country || "").toLowerCase());
    const overlap = [...pset].filter((t) => rec.toks.has(t)).length;
    if (overlap > bestOverlap) { bestOverlap = overlap; best = rec; }
  }
  return best && countries.size === 1 ? { country: best.country, league: best.league, club: best.club } : null;
}
export function setClubLeagues(arr) { _CLUB_INDEX = _buildClubIndex(arr); }
export function getClubLeaguesCount() { return _CLUB_INDEX.list.length; }
// Returns { country, league, club } for a club name, or null when not mapped / ambiguous.
export function resolveClubCountryLeague(team) { return _resolveInIndex(_CLUB_INDEX, team); }
// Given player club names and a club map list, return the names that would NOT resolve.
export function filterUnmappedClubs(teams, list) {
  const index = _buildClubIndex(list);
  const out = [];
  (teams || []).forEach((t) => { if (t && !_resolveInIndex(index, t)) out.push(t); });
  return out;
}

const _norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
// Countries with a single coefficient.
const COEFF_SINGLE = {
  france: 1.0, portugal: 0.8, finland: 0.8, sweden: 1.0, belgium: 0.8,
  netherlands: 0.9, poland: 0.8, scotland: 0.9, ireland: 0.85, austria: 0.85,
  croatia: 0.8, slovenia: 0.8, ukraine: 0.85, switzerland: 0.8, czechia: 0.8,
  iceland: 0.8, japan: 1.0, canada: 1.0, australia: 0.9, mexico: 0.9,
  argentina: 0.9, brazil: 0.9, russia: 0.9,
  china: 0.8, romania: 0.55, hungary: 0.6, latvia: 0.45, belarus: 0.5,
  colombia: 0.75, ecuador: 0.6, venezuela: 0.55, zambia: 0.4, wales: 0.55,
  "northern ireland": 0.6,
};
// Countries with a 1st/2nd division split.
const COEFF_DIV = {
  italy: { d1: 1.0, d2: 0.6 },     // Serie A / Serie B
  england: { d1: 1.5, d2: 1.0 },
  usa: { d1: 1.5, d2: 1.0 },      // NWSL / USL
  germany: { d1: 1.2, d2: 0.9 },  // Bundesliga / Bundesliga 2
  spain: { d1: 1.0, d2: 0.8 },    // Liga F / 2ª
  denmark: { d1: 0.9, d2: 0.7 },
  norway: { d1: 0.9, d2: 0.7 },
};
// Map many country spellings (IT/EN) to a canonical key via keyword match.
const COUNTRY_ALIASES = [
  ["italy", ["italia", "italy", "ital"]], ["france", ["francia", "france"]],
  ["england", ["inghilterra", "england", "english", "britann", "regno unito", "uk", "gran bretagna"]],
  ["spain", ["spagna", "spain", "espan"]], ["germany", ["germania", "germany", "deutsch"]],
  ["usa", ["stati uniti", "usa", "united states", "u.s", "america"]],
  ["portugal", ["portogallo", "portugal"]], ["denmark", ["danimarca", "denmark"]],
  ["norway", ["norvegia", "norway"]], ["finland", ["finlandia", "finland"]],
  ["sweden", ["svezia", "sweden"]], ["belgium", ["belgio", "belgium"]],
  ["netherlands", ["olanda", "paesi bassi", "netherlands", "holland"]],
  ["poland", ["polonia", "poland"]], ["scotland", ["scozia", "scotland"]],
  ["northern ireland", ["irlanda del nord", "northern ireland", "nordirland", "north. ir"]],
  ["ireland", ["irlanda", "ireland", "eire"]], ["austria", ["austria"]],
  ["croatia", ["croazia", "croatia"]], ["slovenia", ["slovenia"]],
  ["ukraine", ["ucraina", "ukraine"]], ["switzerland", ["svizzera", "switzerland", "suisse", "schweiz"]],
  ["czechia", ["ceca", "cechia", "czech"]], ["iceland", ["islanda", "iceland"]],
  ["japan", ["giappone", "japan"]], ["canada", ["canada"]],
  ["australia", ["australia"]], ["mexico", ["messico", "mexico"]],
  ["argentina", ["argentina"]], ["brazil", ["brasile", "brazil", "brasil"]],
  ["russia", ["russia", "russian"]],
  ["china", ["cina", "china", "chinese"]], ["romania", ["romania", "romena", "romanian"]],
  ["hungary", ["ungheria", "hungary", "magyar"]], ["latvia", ["lettonia", "latvia", "latvija"]],
  ["belarus", ["bielorussia", "belarus", "belarusian"]], ["colombia", ["colombia", "colombian"]],
  ["ecuador", ["ecuador"]], ["venezuela", ["venezuela"]], ["zambia", ["zambia"]],
  ["wales", ["galles", "wales", "cymru", "welsh"]],
];
function _countryKey(country) {
  const c = _norm(country);
  if (!c) return null;
  for (const [key, words] of COUNTRY_ALIASES) {
    if (words.some((w) => c.includes(w))) return key;
  }
  return null;
}
function _isSecondDivision(key, league) {
  const l = _norm(league);
  if (!l) return false;
  if (key === "usa") return /\busl\b/.test(l);
  if (key === "england") return /wsl\s*2|championship|\bdivision ?2\b|\bdiv\.? ?2\b|\b2\b/.test(l);
  return /(^|[^0-9])2([^0-9]|$)|\bii\b|second|serie ?b|division ?2|div\.? ?2|2\.? ?(liga|division|divisione|divisao|lega)|bundesliga ?2/.test(l);
}
const _title = (k) => k.charAt(0).toUpperCase() + k.slice(1);
// Flat, human-readable list of built-in entries with their effective coefficient
// (override applied). Used by the management dialog.
export function builtinLeagueRows() {
  const rows = [];
  Object.keys(COEFF_DIV).forEach((k) => {
    rows.push({ id: `${k}:d1`, label: `${_title(k)} — 1ª div`, base: COEFF_DIV[k].d1, coeff: _coeffOverrides[`${k}:d1`] ?? COEFF_DIV[k].d1 });
    rows.push({ id: `${k}:d2`, label: `${_title(k)} — 2ª div`, base: COEFF_DIV[k].d2, coeff: _coeffOverrides[`${k}:d2`] ?? COEFF_DIV[k].d2 });
  });
  Object.keys(COEFF_SINGLE).forEach((k) => {
    rows.push({ id: k, label: _title(k), base: COEFF_SINGLE[k], coeff: _coeffOverrides[k] ?? COEFF_SINGLE[k] });
  });
  return rows.sort((a, b) => a.label.localeCompare(b.label));
}
export function getCustomLeagues() { return _customLeagues; }

export function leagueCoefficient(country, league) {
  const hay = _norm(`${country || ""} ${league || ""}`);
  for (const c of _customLeagues) {
    const kws = Array.isArray(c.match) ? c.match : [];
    if (kws.some((w) => w && hay.includes(_norm(w)))) return Number(c.coeff) || 1.0;
  }
  const key = _countryKey(country);
  if (!key) return 1.0;
  let id, base;
  if (COEFF_DIV[key]) {
    const d2 = _isSecondDivision(key, league);
    id = `${key}:${d2 ? "d2" : "d1"}`;
    base = d2 ? COEFF_DIV[key].d2 : COEFF_DIV[key].d1;
  } else if (key in COEFF_SINGLE) {
    id = key;
    base = COEFF_SINGLE[key];
  } else {
    return 1.0;
  }
  return _coeffOverrides[id] != null ? Number(_coeffOverrides[id]) : base;
}
// Default weights per role, for the editor UI: { role: [{ label, weight }] }
export function defaultFtxWeights() {
  const out = {};
  Object.entries(FTX_ROLE_STATS).forEach(([role, defs]) => {
    out[role] = defs.map((d) => ({ label: d.label, weight: d.weight || 1 }));
  });
  return out;
}

// Resolve the active metric definitions for a role, applying user overrides
// (custom metric set + weights) when present, else the built-in defaults.
export function activeDefsForRole(role, metrics = []) {
  const ov = WEIGHT_OVERRIDES[role];
  if (ov && typeof ov === "object" && Object.keys(ov).length) {
    const map = fullCatalogMap(metrics);
    return Object.entries(ov)
      .map(([label, weight]) => {
        const base = map[label] || METRIC_CATALOG[label];
        return base ? { ...base, weight: Number(weight) || 0 } : null;
      })
      .filter(Boolean);
  }
  return FTX_ROLE_STATS[role] || [];
}

// Resolve the FT X-contributing metric slugs for a role (yellow highlight in the UI).
export function resolveFtxSlugs(role, metrics = []) {
  const defs = activeDefsForRole(role, metrics);
  const out = new Set();
  defs.forEach((d) => {
    if (d.field) out.add(d.field);
    for (const frag of (d.match || [])) {
      const m = metrics.find((x) => x.slug === frag) || metrics.find((x) => x.slug.startsWith(frag)) || metrics.find((x) => x.slug.includes(frag));
      if (m) { out.add(m.slug); break; }
    }
  });
  return out;
}

// Recommended per-role FT X thresholds. value >= hot => "hot target" (green).
export const FTX_HOT = { GK: 21, RCB: 18, LCB: 18, RB: 18, LB: 18, CM: 20, RW: 20, LW: 20, ST: 21, FW: 21 };

export function ftxTier(role, value) {
  if (value == null) return null;
  const hot = FTX_HOT[role] || 20;
  if (value >= hot) return "hot";
  if (value >= hot - 4) return "watch";
  return "low";
}

export function ftxTierColor(role, value) {
  const t = ftxTier(role, value);
  return t === "hot" ? "text-emerald-400" : t === "watch" ? "text-amber-400" : t === "low" ? "text-red-400" : "text-muted-foreground/60";
}

function resolveValue(def, player, metrics) {
  if (def.field && player[def.field] != null && player[def.field] !== "") return Number(player[def.field]);
  for (const frag of def.match) {
    const m = metrics.find((x) => x.slug === frag) || metrics.find((x) => x.slug.startsWith(frag)) || metrics.find((x) => x.slug.includes(frag));
    if (m && player.stats && player.stats[m.slug] != null && player.stats[m.slug] !== "") return Number(player.stats[m.slug]);
  }
  return null;
}

function norm(def, v) {
  if (v == null || Number.isNaN(v)) return null;
  if (def.kind === "pct") {
    const frac = v > 1 ? v / 100 : v;
    return Math.max(0, Math.min(1, frac / (def.ref || 1)));
  }
  if (def.kind === "invrate") {
    // lower is better (e.g. conceded goals/90): v=0 → 1.0, v>=bad → 0
    return Math.max(0, Math.min(1, 1 - v / (def.bad || 1)));
  }
  return Math.max(0, Math.min(1, v / (def.cap || 1)));
}

// Missing-stat penalty. Offensive roles are penalised MORE gently so that top
// attackers with 1-2 missing stats still land in the 20-30 band.
const OFFENSIVE_ROLES = new Set(["CM", "RW", "LW", "ST", "FW"]);
const MISSING_PENALTY = [1, 0.7, 0.5, 0.35, 0.25];
const MISSING_PENALTY_OFF = [1, 0.9, 0.8, 0.62, 0.5];
export function missingPenalty(missing, role) {
  if (missing <= 0) return 1;
  const table = OFFENSIVE_ROLES.has(role) ? MISSING_PENALTY_OFF : MISSING_PENALTY;
  return table[Math.min(missing, table.length - 1)];
}

// Role key-stat breakdown for ANY player, using a given role's active metric defs.
// Returns [{ label, raw, pct }] where pct is 0..100 (% of the role reference) or null if missing.
export function roleStatBreakdown(role, player, metrics = []) {
  const defs = activeDefsForRole(role, metrics);
  return (defs || []).map((d) => {
    const raw = resolveValue(d, player, metrics);
    const n = norm(d, raw);
    return { label: d.label, raw, kind: d.kind, pct: n == null ? null : Math.round(n * 100) };
  });
}

// Resolve the league-difficulty coefficient for a single player's CLUB (1.0 when disabled
// or unknown). Reused by the scouting scatter's "apply league coefficient" Top filter.
export function playerLeagueCoeff(player) {
  if (!LEAGUE_COEFF_ENABLED) return 1;
  const cl = resolveClubCountryLeague(player?.team);
  return cl ? leagueCoefficient(cl.country, cl.league) : UNMAPPED_COEFF;
}

// Same as above but ALWAYS computes the coefficient, ignoring the global FT X enable flag.
// Used by the scatter's own "Coeff. lega" toggle so it works independently of FT X settings.
export function playerLeagueCoeffRaw(player) {
  const cl = resolveClubCountryLeague(player?.team);
  return cl ? leagueCoefficient(cl.country, cl.league) : UNMAPPED_COEFF;
}

// Returns { value: 1..30 | null, parts: [{label, raw, pct}], available }
export function computeXFactor(player, metrics = []) {
  const manualRaw = player?.ftx_manual;
  const hasManual = manualRaw != null && manualRaw !== "" && !Number.isNaN(Number(manualRaw));
  const manualVal = hasManual ? Math.max(1, Math.min(FTX_MAX, Math.round(Number(manualRaw)))) : null;
  const defs = activeDefsForRole(player?.role, metrics);
  if (!defs || !defs.length) return { value: manualVal, parts: [], available: 0, missing: 0, penalty: 1, role: player?.role, manual: hasManual };
  const parts = defs.map((d) => {
    const raw = resolveValue(d, player, metrics);
    const n = norm(d, raw);
    return { label: d.label, raw, pct: n, weight: Number(d.weight) || 0 };
  });
  const present = parts.filter((p) => p.pct != null);
  const missing = parts.length - present.length;
  if (!present.length) return { value: manualVal, parts, available: 0, missing, penalty: 1, role: player.role, manual: hasManual };
  const wsum = present.reduce((a, b) => a + b.weight, 0);
  const mean = present.reduce((a, b) => a + b.pct * b.weight, 0) / wsum;
  const penalty = missingPenalty(missing, player.role);
  const base = Math.max(1, Math.round(mean * FTX_MAX * penalty));
  let coeff = 1;
  if (LEAGUE_COEFF_ENABLED) {
    const cl = resolveClubCountryLeague(player.team);
    coeff = cl ? leagueCoefficient(cl.country, cl.league) : UNMAPPED_COEFF;
  }
  const computed = coeff === 1 ? base : Math.max(1, Math.round(base * coeff));
  return { value: hasManual ? manualVal : computed, baseValue: base, coeff, parts, available: present.length, missing, penalty, role: player.role, manual: hasManual };
}
